import type { AssetVitalityCounts } from '../../../client/IvtHubClient';
import { stackWidths } from './scale';

/**
 * The 资产活跃度 group: three pills whose widths compare the vitality bands.
 *
 * The one figure on this page counted in ASSETS rather than opportunities. Bands
 * come from each asset's own pipeline: nothing open is red, open-but-no-new is
 * amber, new pipeline in the window is green.
 *
 * ⚠️ These three pills are their own scale family — they are not comparable with
 * the industry table's stacked bars. The pills show one row (the total across the
 * industries in scope) split into three, whereas the table normalises ACROSS rows
 * so a row's bar length encodes that row's magnitude. Forcing one shared px/asset
 * on both would need a pill track about twice as wide as the layout has, and the
 * design does not do it either.
 */

/** Width of the track the three pills share, matching the design's 378px column. */
const TRACK_PX = 360;

/**
 * Narrowest a pill may be. The count is drawn INSIDE the pill at 28px, so this is
 * set by the text, not by visual taste: 20px of left inset (measured off the design,
 * where all three counts sit at exactly that offset) plus room for three digits.
 * The design's smallest pill is 82px, holding two.
 */
export const MIN_PILL_PX = 80;

/**
 * Width of an EMPTY band's placeholder — one pill-height, so it draws as a circle with its
 * "0" centred (see `.ivt-dashboard__pill-zero`).
 *
 * Deliberately narrower than MIN_PILL_PX, which is what keeps it from inverting: "none"
 * can never render wider than the smallest real band. It is told apart from a band by its
 * lighter fill rather than by its width, the same way the industry table's bar marks an empty band
 * (`.ivt-dashboard__vbar-seg.is-empty`).
 */
export const ZERO_PILL_PX = 52;

/** The same three words as IndustryTable's legend, so the page names each band once. */
const BANDS = [
  { key: 'green', label: '活跃', className: 'is-green' },
  { key: 'amber', label: '低活', className: 'is-amber' },
  { key: 'red', label: '停摆', className: 'is-red' },
] as const;

interface Props {
  vitality: AssetVitalityCounts | undefined;
}

export function VitalityPills({ vitality }: Props) {
  const counts = BANDS.map((b) => vitality?.[b.key] ?? 0);
  // Placeholders come out of the track first, so the group stays TRACK_PX wide however
  // many bands are empty — without this, each zero would push the row past its column.
  const empty = counts.filter((c) => !(c > 0)).length;
  const widths = stackWidths(counts, TRACK_PX - empty * ZERO_PILL_PX, MIN_PILL_PX);

  return (
    <div className="ivt-dashboard__vitality">
      <div className="ivt-dashboard__kpi-label">资产活跃度</div>
      <div className="ivt-dashboard__pills">
        {BANDS.map((band, i) => (
          <div
            className="ivt-dashboard__pill-band"
            key={band.key}
            style={{ width: widths[i] > 0 ? widths[i] : ZERO_PILL_PX }}
          >
            <div className="ivt-dashboard__pill-label">{band.label}</div>
            {/* A zero band gets a placeholder, NOT a pill. A band-coloured pill wide enough
                to hold "0" would read as a small band; with no shape at all the "0" floated
                on its own and read as a gap in the row. The placeholder is a lighter fill of the
                band's colour, so it still says which band is empty. */}
            {widths[i] > 0 ? (
              <div className={`ivt-dashboard__pill ${band.className}`}>{counts[i]}</div>
            ) : (
              <div className={`ivt-dashboard__pill-zero ${band.className}`}>0</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
